import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getLocalizedText, formatPrice } from '../utils/helpers';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { 
  Eye, EyeOff, Edit, Trash2, RefreshCw, Calendar, 
  AlertTriangle, CheckCircle, Clock, XCircle, Star, Pause
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translations
const t = {
  es: {
    // Status labels
    approved: 'Aprobado',
    pending: 'Pendiente de aprobación',
    rejected: 'Rechazado',
    featured: 'En evidencia',
    paused: 'En pausa',
    expired: 'Finalizado',
    pendingEdit: 'Edición pendiente',
    // Actions
    deactivate: 'Pausar',
    activate: 'Reactivar',
    edit: 'Editar',
    delete: 'Eliminar',
    republish: 'Republicar',
    // Dialogs
    confirmDelete: '¿Estás seguro de que quieres eliminar este anuncio?',
    confirmDeleteTitle: 'Confirmar eliminación',
    deleteWarning: 'Esta acción no se puede deshacer.',
    cancel: 'Cancelar',
    republishTitle: 'Republicar curso',
    republishDesc: 'Ingresa las nuevas fechas para republicar el curso. La republicación requiere aprobación del administrador.',
    startDate: 'Fecha de inicio',
    endDate: 'Fecha de fin (opcional)',
    republishBtn: 'Republicar curso',
    // Messages
    success: {
      toggled: 'Estado actualizado correctamente',
      deleted: 'Anuncio eliminado correctamente',
      republished: 'Curso republicado. Pendiente de aprobación.',
    },
    error: {
      generic: 'Ocurrió un error. Inténtalo de nuevo.',
    }
  }
};

// Status Badge Component - Reusable for both services and courses
function StatusBadges({ item, isCourse = false, language = 'es' }) {
  const lang = t[language] || t.es;
  
  const isApproved = item.is_approved;
  const isRejected = item.is_rejected || item.edit_rejected_at;
  const isPaused = !item.is_active && item.deactivated_by_user;
  const isFeatured = item.featured;
  const hasPendingEdit = item.edit_pending;
  const isExpired = isCourse && item.start_date && new Date(item.start_date) < new Date();
  
  // Determine the primary approval state (mutually exclusive)
  // Priority: Expired > Paused > Pending Edit > Rejected > Approved > Pending
  const getApprovalState = () => {
    if (isExpired) return 'expired';
    if (isPaused) return 'paused';
    if (hasPendingEdit) return 'pending_edit';
    if (isRejected && !isApproved) return 'rejected';
    if (isApproved) return 'approved';
    return 'pending';
  };
  
  const approvalState = getApprovalState();
  
  return (
    <div className="flex flex-wrap gap-1.5 mb-3">
      {/* Featured - Star (Gold) - Can be shown alongside other states */}
      {isFeatured && !isExpired && !isPaused && (
        <Badge className="bg-amber-500 text-white text-xs flex items-center gap-1" data-testid="badge-featured">
          <Star className="h-3 w-3 fill-current" /> {lang.featured}
        </Badge>
      )}
      
      {/* Expired - Only for courses (Dark Gray) */}
      {approvalState === 'expired' && (
        <Badge className="bg-gray-600 text-white text-xs flex items-center gap-1" data-testid="badge-expired">
          <Clock className="h-3 w-3" /> {lang.expired}
        </Badge>
      )}
      
      {/* Paused by user (Gray with pause icon) */}
      {approvalState === 'paused' && (
        <Badge className="bg-gray-500 text-white text-xs flex items-center gap-1" data-testid="badge-paused">
          <Pause className="h-3 w-3" /> {lang.paused}
        </Badge>
      )}
      
      {/* Pending Edit (Orange) */}
      {approvalState === 'pending_edit' && (
        <Badge className="bg-orange-500 text-white text-xs flex items-center gap-1" data-testid="badge-pending-edit">
          <Clock className="h-3 w-3" /> {lang.pendingEdit}
        </Badge>
      )}
      
      {/* Rejected (Red) */}
      {approvalState === 'rejected' && (
        <Badge className="bg-red-500 text-white text-xs flex items-center gap-1" data-testid="badge-rejected">
          <XCircle className="h-3 w-3" /> {lang.rejected}
        </Badge>
      )}
      
      {/* Approved (Green) */}
      {approvalState === 'approved' && (
        <Badge className="bg-green-500 text-white text-xs flex items-center gap-1" data-testid="badge-approved">
          <CheckCircle className="h-3 w-3" /> {lang.approved}
        </Badge>
      )}
      
      {/* Pending Approval (Yellow) */}
      {approvalState === 'pending' && (
        <Badge className="bg-yellow-500 text-white text-xs flex items-center gap-1" data-testid="badge-pending">
          <Clock className="h-3 w-3" /> {lang.pending}
        </Badge>
      )}
    </div>
  );
}

// Service Card with Management Actions
export function ServiceCard({ service, language = 'es', onUpdate }) {
  const { getAuthHeaders } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const lang = t[language] || t.es;

  const isPaused = !service.is_active && service.deactivated_by_user;

  const handleToggleActive = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/services/${service.id}/toggle-active`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(lang.success.toggled);
      onUpdate?.();
    } catch (error) {
      toast.error(lang.error.generic);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/services/${service.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success(lang.success.deleted);
      setShowDeleteDialog(false);
      onUpdate?.();
    } catch (error) {
      toast.error(lang.error.generic);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/edit-service/${service.id}`);
  };

  return (
    <>
      <div 
        className={`p-4 border rounded-xl transition-all ${isPaused ? 'opacity-70 bg-gray-50' : 'bg-white'}`}
        data-testid={`service-card-${service.id}`}
      >
        {/* Header with image */}
        {service.images?.[0] && (
          <div className="aspect-video rounded-lg overflow-hidden mb-3 relative">
            <img 
              src={service.images[0]} 
              alt={getLocalizedText(service.title, language)}
              className="w-full h-full object-cover"
            />
            {/* Featured star overlay */}
            {service.featured && (
              <div className="absolute top-2 right-2 bg-amber-500 rounded-full p-1.5">
                <Star className="h-4 w-4 text-white fill-current" />
              </div>
            )}
          </div>
        )}
        
        {/* Title and Price */}
        <h4 className="font-semibold text-primary mb-1">
          {getLocalizedText(service.title, language)}
        </h4>
        <p className="text-sm text-secondary mb-2">{formatPrice(service.price)}</p>
        
        {/* Status Badges - Using the reusable component */}
        <StatusBadges item={service} isCourse={false} language={language} />
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleToggleActive}
            disabled={loading}
            className={`flex-1 min-w-[80px] ${isPaused ? 'border-green-500 text-green-600 hover:bg-green-50' : ''}`}
            data-testid={`toggle-service-${service.id}`}
          >
            {isPaused ? <Eye className="h-3.5 w-3.5 mr-1" /> : <Pause className="h-3.5 w-3.5 mr-1" />}
            {isPaused ? lang.activate : lang.deactivate}
          </Button>
          
          <Button
            size="sm"
            variant="outline"
            onClick={handleEdit}
            disabled={loading}
            className="flex-1 min-w-[80px]"
            data-testid={`edit-service-${service.id}`}
          >
            <Edit className="h-3.5 w-3.5 mr-1" /> {lang.edit}
          </Button>
          
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
            disabled={loading}
            className="flex-1 min-w-[80px]"
            data-testid={`delete-service-${service.id}`}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> {lang.delete}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {lang.confirmDeleteTitle}
            </DialogTitle>
            <DialogDescription>
              {lang.confirmDelete}
              <br />
              <span className="text-red-500 font-medium">{lang.deleteWarning}</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {lang.cancel}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {lang.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// Course Card with Management Actions
export function CourseCard({ course, language = 'es', onUpdate }) {
  const { getAuthHeaders } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRepublishDialog, setShowRepublishDialog] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const lang = t[language] || t.es;

  const isPaused = !course.is_active && course.deactivated_by_user;
  const isExpired = course.start_date && new Date(course.start_date) < new Date();

  const handleToggleActive = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/courses/${course.id}/toggle-active`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(lang.success.toggled);
      onUpdate?.();
    } catch (error) {
      toast.error(lang.error.generic);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/courses/${course.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success(lang.success.deleted);
      setShowDeleteDialog(false);
      onUpdate?.();
    } catch (error) {
      toast.error(lang.error.generic);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/edit-course/${course.id}`);
  };

  const handleRepublish = async () => {
    if (!newStartDate) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: newStartDate });
      if (newEndDate) params.append('end_date', newEndDate);
      
      await axios.post(
        `${API_URL}/api/courses/${course.id}/republish?${params.toString()}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(lang.success.republished);
      setShowRepublishDialog(false);
      setNewStartDate('');
      setNewEndDate('');
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || lang.error.generic);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div 
        className={`p-4 border rounded-xl transition-all ${isPaused || isExpired ? 'opacity-70 bg-gray-50' : 'bg-white'}`}
        data-testid={`course-card-${course.id}`}
      >
        {/* Header with image */}
        {course.images?.[0] && (
          <div className="aspect-video rounded-lg overflow-hidden mb-3 relative">
            <img 
              src={course.images[0]} 
              alt={getLocalizedText(course.title, language)}
              className="w-full h-full object-cover"
            />
            {/* Featured star overlay */}
            {course.featured && (
              <div className="absolute top-2 right-2 bg-amber-500 rounded-full p-1.5">
                <Star className="h-4 w-4 text-white fill-current" />
              </div>
            )}
          </div>
        )}
        
        {/* Title and Price */}
        <h4 className="font-semibold text-primary mb-1">
          {getLocalizedText(course.title, language)}
        </h4>
        <p className="text-sm text-secondary mb-1">{formatPrice(course.price)}</p>
        
        {/* Dates */}
        {course.start_date && (
          <p className="text-xs text-secondary mb-2 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(course.start_date).toLocaleDateString('es-ES')}
            {course.end_date && ` - ${new Date(course.end_date).toLocaleDateString('es-ES')}`}
          </p>
        )}
        
        {/* Status Badges - Using the reusable component */}
        <StatusBadges item={course} isCourse={true} language={language} />
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {isExpired ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRepublishDialog(true)}
                disabled={loading}
                className="flex-1 min-w-[100px] border-green-500 text-green-600 hover:bg-green-50"
                data-testid={`republish-course-${course.id}`}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> {lang.republish}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={loading}
                className="flex-1 min-w-[80px]"
                data-testid={`delete-course-${course.id}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> {lang.delete}
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={handleToggleActive}
                disabled={loading}
                className={`flex-1 min-w-[80px] ${isPaused ? 'border-green-500 text-green-600 hover:bg-green-50' : ''}`}
                data-testid={`toggle-course-${course.id}`}
              >
                {isPaused ? <Eye className="h-3.5 w-3.5 mr-1" /> : <Pause className="h-3.5 w-3.5 mr-1" />}
                {isPaused ? lang.activate : lang.deactivate}
              </Button>
              
              <Button
                size="sm"
                variant="outline"
                onClick={handleEdit}
                disabled={loading}
                className="flex-1 min-w-[80px]"
                data-testid={`edit-course-${course.id}`}
              >
                <Edit className="h-3.5 w-3.5 mr-1" /> {lang.edit}
              </Button>
              
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={loading}
                className="flex-1 min-w-[80px]"
                data-testid={`delete-course-${course.id}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> {lang.delete}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {lang.confirmDeleteTitle}
            </DialogTitle>
            <DialogDescription>
              {lang.confirmDelete}
              <br />
              <span className="text-red-500 font-medium">{lang.deleteWarning}</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {lang.cancel}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {lang.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Republish Dialog */}
      <Dialog open={showRepublishDialog} onOpenChange={setShowRepublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-green-600" />
              {lang.republishTitle}
            </DialogTitle>
            <DialogDescription>
              {lang.republishDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">{lang.startDate} *</label>
              <Input
                type="date"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="republish-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{lang.endDate}</label>
              <Input
                type="date"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                min={newStartDate || new Date().toISOString().split('T')[0]}
                data-testid="republish-end-date"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRepublishDialog(false)}>
              {lang.cancel}
            </Button>
            <Button 
              onClick={handleRepublish} 
              disabled={loading || !newStartDate}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-republish-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> {lang.republishBtn}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default { ServiceCard, CourseCard };
